import { type Change, diffArrays } from 'diff';

// Compares JSON by key path, so every value is diffed against the value at the same
// path instead of against whichever sibling line the text aligner happened to pair it with.

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

type LineState = 'added' | 'removed' | 'unchanged';

const INDENT = '  ';

export type JsonChangeSummary = {
  added: number;
  removed: number;
  changed: number;
};

export function isJsonObject(value: JsonValue): value is { [key: string]: JsonValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function line(state: LineState, text: string): Change {
  return { value: text, count: 1, added: state === 'added', removed: state === 'removed' };
}

function isEqual(a: JsonValue, b: JsonValue): boolean {
  return serialize(a) === serialize(b);
}

function serialize(value: JsonValue): string {
  if (isJsonObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${serialize(value[key])}`)
      .join(',')}}`;
  }
  if (Array.isArray(value)) {
    return `[${value.map(serialize).join(',')}]`;
  }
  return JSON.stringify(value) ?? 'null';
}

function valueLines(state: LineState, indent: string, prefix: string, value: JsonValue, comma: boolean): Change[] {
  const suffix = comma ? ',' : '';

  if (isJsonObject(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return [line(state, `${indent}${prefix}{}${suffix}`)];
    }
    return [
      line(state, `${indent}${prefix}{`),
      ...keys.flatMap((key, index) =>
        valueLines(state, indent + INDENT, `${JSON.stringify(key)}: `, value[key], index < keys.length - 1),
      ),
      line(state, `${indent}}${suffix}`),
    ];
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [line(state, `${indent}${prefix}[]${suffix}`)];
    }
    return [
      line(state, `${indent}${prefix}[`),
      ...value.flatMap((item, index) => valueLines(state, indent + INDENT, '', item, index < value.length - 1)),
      line(state, `${indent}]${suffix}`),
    ];
  }

  return [line(state, `${indent}${prefix}${JSON.stringify(value) ?? 'null'}${suffix}`)];
}

// Union of both key sets in the updated document's order, anchoring each removed key
// after the last key the two documents share.
function mergeKeys(originalKeys: string[], updatedKeys: string[]): string[] {
  const updatedKeySet = new Set(updatedKeys);
  const removedByAnchor = new Map<string, string[]>();
  const leadingRemoved: string[] = [];
  let anchor: string | undefined;

  for (const key of originalKeys) {
    if (updatedKeySet.has(key)) {
      anchor = key;
      continue;
    }
    if (anchor === undefined) {
      leadingRemoved.push(key);
      continue;
    }
    removedByAnchor.set(anchor, [...(removedByAnchor.get(anchor) ?? []), key]);
  }

  return [...leadingRemoved, ...updatedKeys.flatMap((key) => [key, ...(removedByAnchor.get(key) ?? [])])];
}

type ArrayAlignment =
  | { kind: 'keep'; originalIndex: number; updatedIndex: number }
  | { kind: 'replace'; originalIndex: number; updatedIndex: number }
  | { kind: 'remove'; originalIndex: number }
  | { kind: 'add'; updatedIndex: number };

// Two values are similar enough to diff against each other when they are both arrays, or
// both objects that agree on at least half of their keys.
function isSimilar(original: JsonValue, updated: JsonValue): boolean {
  if (isJsonObject(original) && isJsonObject(updated)) {
    const keys = new Set([...Object.keys(original), ...Object.keys(updated)]);
    let shared = 0;
    for (const key of keys) {
      if (Object.hasOwn(original, key) && Object.hasOwn(updated, key) && isEqual(original[key], updated[key])) {
        shared++;
      }
    }
    return shared * 2 >= keys.size;
  }

  return Array.isArray(original) && Array.isArray(updated);
}

// Elements the exact-match alignment dropped and re-added are paired back up positionally so
// an element that merely gained a key is diffed instead of rewritten.
function pairReplacements(steps: ArrayAlignment[], original: JsonValue[], updated: JsonValue[]): ArrayAlignment[] {
  const aligned: ArrayAlignment[] = [];
  let index = 0;

  while (index < steps.length) {
    const step = steps[index];
    if (step.kind === 'keep') {
      aligned.push(step);
      index++;
      continue;
    }

    const removedIndexes: number[] = [];
    const addedIndexes: number[] = [];
    while (index < steps.length && steps[index].kind !== 'keep') {
      const run = steps[index];
      if (run.kind === 'remove') {
        removedIndexes.push(run.originalIndex);
      } else if (run.kind === 'add') {
        addedIndexes.push(run.updatedIndex);
      }
      index++;
    }

    for (let offset = 0; offset < Math.max(removedIndexes.length, addedIndexes.length); offset++) {
      const originalIndex = removedIndexes[offset];
      const updatedIndex = addedIndexes[offset];

      if (
        originalIndex !== undefined &&
        updatedIndex !== undefined &&
        isSimilar(original[originalIndex], updated[updatedIndex])
      ) {
        aligned.push({ kind: 'replace', originalIndex, updatedIndex });
        continue;
      }
      if (originalIndex !== undefined) {
        aligned.push({ kind: 'remove', originalIndex });
      }
      if (updatedIndex !== undefined) {
        aligned.push({ kind: 'add', updatedIndex });
      }
    }
  }

  return aligned;
}

function alignArrays(original: JsonValue[], updated: JsonValue[]): ArrayAlignment[] {
  const changes = diffArrays(original.map(serialize), updated.map(serialize));
  const alignment: ArrayAlignment[] = [];
  let originalIndex = 0;
  let updatedIndex = 0;

  for (const change of changes) {
    for (let count = change.value.length; count > 0; count--) {
      if (change.added) {
        alignment.push({ kind: 'add', updatedIndex: updatedIndex++ });
      } else if (change.removed) {
        alignment.push({ kind: 'remove', originalIndex: originalIndex++ });
      } else {
        alignment.push({ kind: 'keep', originalIndex: originalIndex++, updatedIndex: updatedIndex++ });
      }
    }
  }

  return pairReplacements(alignment, original, updated);
}

// An equal value can still need different trailing commas on each side. Only the line carrying
// the comma is emitted as a removed/added pair; the rest of the value stays unchanged.
function unchangedLines(
  indent: string,
  prefix: string,
  value: JsonValue,
  originalComma: boolean,
  updatedComma: boolean,
): Change[] {
  const lines = valueLines('unchanged', indent, prefix, value, originalComma);
  if (originalComma === updatedComma) {
    return lines;
  }

  const lastText = lines[lines.length - 1].value;
  const withoutComma = originalComma ? lastText.slice(0, -1) : lastText;

  return [
    ...lines.slice(0, -1),
    line('removed', lastText),
    line('added', updatedComma ? `${withoutComma},` : withoutComma),
  ];
}

function closingLines(indent: string, closer: string, originalComma: boolean, updatedComma: boolean): Change[] {
  if (originalComma === updatedComma) {
    return [line('unchanged', `${indent}${closer}${originalComma ? ',' : ''}`)];
  }
  return [
    line('removed', `${indent}${closer}${originalComma ? ',' : ''}`),
    line('added', `${indent}${closer}${updatedComma ? ',' : ''}`),
  ];
}

function diffValueLines(
  indent: string,
  prefix: string,
  original: JsonValue,
  updated: JsonValue,
  originalComma: boolean,
  updatedComma: boolean,
): Change[] {
  if (isEqual(original, updated)) {
    return unchangedLines(indent, prefix, original, originalComma, updatedComma);
  }

  if (isJsonObject(original) && isJsonObject(updated)) {
    const merged = mergeKeys(Object.keys(original), Object.keys(updated));
    const lastOriginalKey = merged.filter((key) => Object.hasOwn(original, key)).pop();
    const lastUpdatedKey = merged.filter((key) => Object.hasOwn(updated, key)).pop();
    const childIndent = indent + INDENT;

    const children = merged.flatMap((key) => {
      const childPrefix = `${JSON.stringify(key)}: `;
      const childOriginalComma = key !== lastOriginalKey;
      const childUpdatedComma = key !== lastUpdatedKey;

      if (!Object.hasOwn(updated, key)) {
        return valueLines('removed', childIndent, childPrefix, original[key], childOriginalComma);
      }
      if (!Object.hasOwn(original, key)) {
        return valueLines('added', childIndent, childPrefix, updated[key], childUpdatedComma);
      }
      return diffValueLines(
        childIndent,
        childPrefix,
        original[key],
        updated[key],
        childOriginalComma,
        childUpdatedComma,
      );
    });

    return [
      line('unchanged', `${indent}${prefix}{`),
      ...children,
      ...closingLines(indent, '}', originalComma, updatedComma),
    ];
  }

  if (Array.isArray(original) && Array.isArray(updated)) {
    const alignment = alignArrays(original, updated);
    const childIndent = indent + INDENT;
    const lastOriginal = alignment.filter((step) => step.kind !== 'add').pop();
    const lastUpdated = alignment.filter((step) => step.kind !== 'remove').pop();

    const items = alignment.flatMap((step) => {
      const hasOriginalComma = step !== lastOriginal;
      const hasUpdatedComma = step !== lastUpdated;

      if (step.kind === 'remove') {
        return valueLines('removed', childIndent, '', original[step.originalIndex], hasOriginalComma);
      }
      if (step.kind === 'add') {
        return valueLines('added', childIndent, '', updated[step.updatedIndex], hasUpdatedComma);
      }
      if (step.kind === 'replace') {
        return diffValueLines(
          childIndent,
          '',
          original[step.originalIndex],
          updated[step.updatedIndex],
          hasOriginalComma,
          hasUpdatedComma,
        );
      }
      return unchangedLines(childIndent, '', original[step.originalIndex], hasOriginalComma, hasUpdatedComma);
    });

    return [
      line('unchanged', `${indent}${prefix}[`),
      ...items,
      ...closingLines(indent, ']', originalComma, updatedComma),
    ];
  }

  return [
    ...valueLines('removed', indent, prefix, original, originalComma),
    ...valueLines('added', indent, prefix, updated, updatedComma),
  ];
}

export function diffJsonStructurally(original: JsonValue, updated: JsonValue): Change[] {
  return diffValueLines('', '', original, updated, false, false);
}

export function parseJsonPair(original: string, updated: string): [JsonValue, JsonValue] | undefined {
  try {
    return [JSON.parse(original) as JsonValue, JSON.parse(updated) as JsonValue];
  } catch {
    return undefined;
  }
}

export function summarizeJsonChanges(original: JsonValue, updated: JsonValue): JsonChangeSummary {
  const summary: JsonChangeSummary = { added: 0, removed: 0, changed: 0 };

  const walk = (originalValue: JsonValue, updatedValue: JsonValue) => {
    if (isEqual(originalValue, updatedValue)) {
      return;
    }

    if (isJsonObject(originalValue) && isJsonObject(updatedValue)) {
      for (const key of new Set([...Object.keys(originalValue), ...Object.keys(updatedValue)])) {
        if (!Object.hasOwn(updatedValue, key)) {
          summary.removed++;
        } else if (!Object.hasOwn(originalValue, key)) {
          summary.added++;
        } else {
          walk(originalValue[key], updatedValue[key]);
        }
      }
      return;
    }

    if (Array.isArray(originalValue) && Array.isArray(updatedValue)) {
      for (const step of alignArrays(originalValue, updatedValue)) {
        if (step.kind === 'remove') {
          summary.removed++;
        } else if (step.kind === 'add') {
          summary.added++;
        } else if (step.kind === 'replace') {
          walk(originalValue[step.originalIndex], updatedValue[step.updatedIndex]);
        }
      }
      return;
    }

    summary.changed++;
  };

  walk(original, updated);

  return summary;
}
